import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Upload, LogOut, ChevronDown, FileText, Calendar, Trash2, BookOpen, Clock, Brain, Shield, Settings } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : (() => {
    console.warn('[Saige] VITE_API_URL not set. Using same-origin for API calls.');
    return '';
})());

const AccountDropdown = ({ userProfile, onLogout, onUploadDocs, onProfileSettings, documents = [], onDeleteDoc, onRefreshDocs }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getDocIcon = (type) => {
        switch (type) {
            case 'syllabus': return <BookOpen size={14} className="text-blue-500" />;
            case 'bell_schedule': return <Clock size={14} className="text-green-500" />;
            case 'school_calendar': return <Calendar size={14} className="text-purple-500" />;
            case 'lesson_template': return <Brain size={14} className="text-amber-500" />;
            case 'campus_handbook': return <Shield size={14} className="text-red-500" />;
            case 'district_handbook': return <Settings size={14} className="text-slate-500" />;
            case 'custom': return <FileText size={14} className="text-amber-500" />;
            default: return <FileText size={14} className="text-slate-400" />;
        }
    };

    const getDocLabel = (type) => {
        switch (type) {
            case 'syllabus': return 'Syllabus';
            case 'bell_schedule': return 'Bell Schedule';
            case 'school_calendar': return 'Calendar';
            case 'lesson_template': return 'Lesson Template';
            case 'campus_handbook': return 'Campus Handbook';
            case 'district_handbook': return 'District Handbook';
            case 'custom': return 'Custom';
            default: return 'Document';
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Trigger Button - Icon Only */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-1 hover:scale-105 transition-transform"
            >
                <div className="w-8 h-8 bg-gradient-to-tr from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-lg ring-2 ring-white">
                    {userProfile.name?.[0]?.toUpperCase() || 'T'}
                </div>
                <ChevronDown
                    size={12}
                    className={`text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute bottom-full left-0 mb-2 w-72 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden z-50"
                    >
                        {/* Profile Header */}
                        <div className="p-4 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-gradient-to-tr from-yellow-400 to-amber-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                    {userProfile.name?.[0] || 'T'}
                                </div>
                                <div>
                                    <p className="font-bold text-slate-800">{userProfile.name}</p>
                                    <p className="text-xs text-slate-500">{userProfile.email}</p>
                                </div>
                            </div>
                        </div>

                        {/* My Documents Section */}
                        <div className="p-3 border-b border-slate-100">
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">My Documents</p>
                                <button
                                    onClick={() => {
                                        onUploadDocs?.();
                                        setIsOpen(false);
                                    }}
                                    className="text-xs text-amber-600 font-medium hover:text-amber-700"
                                >
                                    + Add
                                </button>
                            </div>

                            {documents.length === 0 ? (
                                <p className="text-xs text-slate-400 italic py-2">No documents uploaded yet</p>
                            ) : (
                                <div className="space-y-1">
                                    {documents.map((doc) => (
                                        <div key={doc.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg group">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                {getDocIcon(doc.type)}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs font-medium text-slate-700 truncate">{doc.name}</p>
                                                    <p className="text-[10px] text-slate-400">{getDocLabel(doc.type)}</p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onDeleteDoc?.(doc.id);
                                                }}
                                                className="p-1 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                                title="Delete document"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Menu Items */}
                        <div className="p-2">
                            <button
                                onClick={() => {
                                    onProfileSettings?.();
                                    setIsOpen(false);
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 transition-colors text-left"
                            >
                                <User size={18} className="text-slate-400" />
                                <span className="text-sm font-medium">Profile Settings</span>
                            </button>

                            <div className="my-2 border-t border-slate-100"></div>

                            <button
                                onClick={() => {
                                    onLogout?.();
                                    setIsOpen(false);
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 hover:bg-red-50 transition-colors text-left"
                            >
                                <LogOut size={18} />
                                <span className="text-sm font-medium">Log Out</span>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AccountDropdown;
